import * as THREE from "three";
import { surfaceColumn, createSurfaceColumn } from "@hyperlight/shared";
import type { Planet } from "../world/planet";
import type { VehicleGround, VehicleGroundSample } from "./groundVehicle";
import { TerrainContacts } from '../world/terrainContacts';

const column = createSurfaceColumn();

export class PlanetVehicleGround implements VehicleGround {
  private dir = new THREE.Vector3();
  private localA = new THREE.Vector3();
  private localB = new THREE.Vector3();
  private normalArray = [0, 1, 0];
  private hitNormal = new THREE.Vector3();
  private localResolved = { x: 0, y: 0, z: 0 };
  private terrainContacts = new TerrainContacts();
  private still = new THREE.Vector3();
  private feet = new THREE.Vector3();

  constructor(readonly planet: Planet) {}

  get center(): THREE.Vector3 { return this.planet.pose.center; }
  get ready(): boolean { return this.planet.terrainSolidsReady !== false; }

  /**
   * Local air density as a fraction of sea level — the whole flight ceiling.
   *
   * The profile is a smoothstep from 1 at sea level to 0 at the flight ceiling
   * H. H is NOT the planet's visual atmosphere height directly: that shell is
   * radius x 0.04-0.08, which on a small world is under a hundred metres and
   * would cap flight at the treetops. Three times the shell, floored at 480 m,
   * keeps flying worth doing everywhere while leaving the shell as the visual
   * cue for "air ends around here". Airless worlds return 0 — a flyer there is
   * an ordinary hover vehicle, which is a design decision, not an accident.
   */
  airDensityAt(worldPoint: THREE.Vector3): number {
    const atmosphere = this.planet.def.atmosphere;
    if (!atmosphere) return 0;
    const baseline = this.planet.def.seaRadius ?? this.planet.def.radius;
    const altitude = worldPoint.distanceTo(this.center) - baseline;
    if (altitude <= 0) return 1;
    const ceiling = Math.max(atmosphere.height * 3, 480);
    const x = Math.min(1, altitude / ceiling);
    return 1 - x * x * (3 - 2 * x);
  }

  sample(worldPoint: THREE.Vector3, out: VehicleGroundSample): boolean {
    if (!this.ready) return false;
    this.dir.copy(worldPoint).sub(this.center);
    const r = this.dir.length();
    if (r < 1) return false;
    this.dir.divideScalar(r);
    // Hovercraft ride the exposed boundary, including lava; this grants no
    // swimming, foot support, or heat protection to their occupants.
    this.localA.copy(this.dir).applyQuaternion(this.planet.pose.invQuat);
    surfaceColumn(this.planet.sampler, this.planet.def, this.localA.x, this.localA.y, this.localA.z, column);
    const onLiquid = column.liquidDepth > 0.05;
    let groundR = onLiquid ? column.liquidR : column.supportR;
    const cliffR = this.terrainContacts.support(this.planet, worldPoint, .03, Math.max(2, r - groundR));
    const onCliff = cliffR !== null && cliffR > groundR;
    if (onCliff) groundR = cliffR;
    out.point.copy(this.center).addScaledVector(this.dir, groundR);
    if (onCliff) out.normal.copy(this.terrainContacts.normal);
    else if (onLiquid) out.normal.copy(this.dir);
    else {
      this.planet.surfaceNormalWorld(this.dir, this.normalArray, 1.2);
      out.normal.set(this.normalArray[0], this.normalArray[1], this.normalArray[2]).normalize();
    }
    return true;
  }

  sweep(from: THREE.Vector3, to: THREE.Vector3, radius: number): { t: number; normal: THREE.Vector3 } | null {
    const colliders = this.planet.settlementColliders;
    this.localA.copy(from).sub(this.center).applyQuaternion(this.planet.pose.invQuat);
    this.localB.copy(to).sub(this.center).applyQuaternion(this.planet.pose.invQuat);
    const hit = colliders?.segmentCast(
      this.localA.x, this.localA.y, this.localA.z,
      this.localB.x, this.localB.y, this.localB.z,
      radius,
    );
    const cliffT = this.terrainContacts.sweep(this.planet, from, to, radius);
    if (cliffT !== null && (!hit || cliffT < hit.t)) {
      this.hitNormal.copy(this.terrainContacts.normal);
      return { t: cliffT, normal: this.hitNormal };
    }
    if (!hit) return null;
    this.hitNormal.set(...hit.n).applyQuaternion(this.planet.pose.quat).normalize();
    return { t: hit.t, normal: this.hitNormal };
  }

  resolveObstacle(position: THREE.Vector3, radius: number, out: THREE.Vector3): boolean {
    this.localA.copy(position).sub(this.center).applyQuaternion(this.planet.pose.invQuat);
    const scatter = this.planet.resolveScatterCollision(
      this.localA.x, this.localA.y, this.localA.z, radius, this.localResolved,
    );
    if (scatter) out.set(this.localResolved.x, this.localResolved.y, this.localResolved.z)
      .applyQuaternion(this.planet.pose.quat).add(this.center);
    else out.copy(position);
    // A sphere is the degenerate capsule; its feet anchor is one radius below its centre.
    this.dir.copy(out).sub(this.center).normalize();
    this.feet.copy(out).addScaledVector(this.dir, -radius);
    this.still.set(0, 0, 0);
    const cliff = this.terrainContacts.move(this.planet, this.feet, this.feet, this.still, 2 * radius, radius);
    if (cliff) out.copy(this.feet).addScaledVector(this.dir, radius);
    return scatter || cliff;
  }
}
